import type {
  ApiClient,
  ApiError,
  SchemaDiagnostic,
} from "../../shared/api/client";
import type { CompiledManifest } from "../../shared/api/generated/contracts";
import type { MessageKey } from "../../shared/i18n";

export interface DraftState {
  manifest: CompiledManifest;
  draftRevision: number;
  /** `rollback` when the Draft was built from a prior version. */
  reason?: "user_confirmed" | "rollback";
  status: "preview" | "publishing" | "published" | "error";
  error?: string;
  /** Set once published: the version the Draft became. */
  versionNumber?: number | null;
  backfilledRecords?: number;
}

export async function publishDraft(
  api: ApiClient,
  abilityId: string,
  actorId: string,
  draft: DraftState,
): Promise<DraftState> {
  if (draft.status !== "preview")
    throw new Error("only a preview can be published");
  const published = await api.publish(
    abilityId,
    actorId,
    draft.draftRevision,
    draft.reason,
  );
  return {
    ...draft,
    status: "published",
    versionNumber: published.versionNumber,
    backfilledRecords: published.backfilledRecords,
  };
}

/** The parts a draft would break, when publishing was refused for that. */
export function incompatibleChanges(cause: unknown): SchemaDiagnostic[] | null {
  if (!(cause instanceof Error)) return null;
  const detail = cause.cause as Partial<ApiError> | undefined;
  return detail?.code === "schema_incompatible"
    ? (detail.diagnostics ?? [])
    : null;
}

const CHANGE_KEYS: Record<string, MessageKey> = {
  field_removed: "draft.change.field_removed",
  field_type_changed: "draft.change.field_type_changed",
  collection_removed: "draft.change.collection_removed",
  required_field_without_default: "draft.change.required_field_without_default",
};

/**
 * "Удалено поле «Высота, см»": the change named with the label the owner
 * knows, looked up in the published manifest first and the draft second.
 */
export function describeChange(
  diagnostic: SchemaDiagnostic,
  manifests: (CompiledManifest | undefined)[],
): { key: MessageKey; subject: string } {
  const [, collectionId, , fieldId] =
    /^\/collections\/([^/]+)(\/fields\/([^/]+))?/.exec(diagnostic.path) ?? [];
  const collections = manifests.flatMap(
    (manifest) => manifest?.collections ?? [],
  );
  const collection = collections.find((item) => item.id === collectionId);
  const field = collections
    .filter((item) => item.id === collectionId)
    .flatMap((item) => item.fields)
    .find((item) => item.id === fieldId);
  return {
    key: CHANGE_KEYS[diagnostic.code] ?? "draft.change.other",
    subject: field?.label ?? fieldId ?? collection?.label ?? collectionId ?? "",
  };
}

type PublishRefusal =
  "base_stale" | "no_changes" | "backfill_too_large" | "incompatible";

const REFUSAL_CODES: Record<string, PublishRefusal> = {
  draft_base_stale: "base_stale",
  draft_has_no_changes: "no_changes",
  backfill_exceeds_record_limit: "backfill_too_large",
  schema_incompatible: "incompatible",
};

/** Why the server refused to publish, by the codes it names (AGE-005/006). */
export function publishRefusal(cause: unknown): PublishRefusal | null {
  if (!(cause instanceof Error)) return null;
  const detail = cause.cause as Partial<ApiError> | undefined;
  const code = detail?.code;
  return code === undefined ? null : (REFUSAL_CODES[code] ?? null);
}

/** The message that explains one refusal to the owner. */
export const REFUSAL_MESSAGES: Record<
  Exclude<PublishRefusal, "incompatible">,
  MessageKey
> = {
  base_stale: "draft.staleBase.title",
  no_changes: "draft.noChanges",
  backfill_too_large: "draft.backfillTooLarge",
};
